import React, { useState } from "react";
import { CircularProgress } from "@mui/material";
import {
  InheritTextInputField,
  InputWithRightButtonWrapper,
  InputRightButton,
} from "./styled";

/**
 * Props for the InputWithButton component
 */
interface InputWithButtonProps {
  /** The current value of the input field */
  value?: string;
  /** Placeholder text displayed when input is empty */
  placeholder: string;
  /** Test identifier for the input field */
  "data-testid"?: string;
  /** Callback function triggered when input value changes */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback function triggered when button is clicked. Can be async. */
  onButtonClick?: () => void | Promise<void>;
  /** Content to display inside the button (text or React node) */
  buttonLabel?: React.ReactNode;
  /** Whether the button should be disabled */
  buttonDisabled?: boolean;
  /** Tooltip text shown on button hover */
  buttonTooltip?: string;
  /** Test identifier for the button */
  buttonDataTestId?: string;
  /** Maximum width for the input wrapper */
  inputMaxWidth?: string;
  /** Whether to show the button. Default is true */
  showButton?: boolean;
}

/**
 * InputWithButton Component
 *
 * A reusable input field with an integrated action button positioned on the right side.
 * The button shows a loading spinner while async operations are in progress.
 *
 * Features:
 * - Async button click handling with loading state
 * - Text overflow with ellipsis for long input values
 * - Customizable button label and tooltip
 * - Automatic button disabling during loading
 * - Flexible styling options
 *
 * @example
 * ```tsx
 * <InputWithButton
 *   placeholder="Enter French text"
 *   value={frenchText}
 *   onChange={(e) => setFrenchText(e.target.value)}
 *   onButtonClick={handleTranslate}
 *   buttonLabel="Translate"
 *   buttonDisabled={!englishText}
 *   buttonTooltip="Click to translate"
 * />
 * ```
 */
export const InputWithButton: React.FC<InputWithButtonProps> = ({
  value,
  placeholder,
  onChange,
  onButtonClick,
  "data-testid": dataTestId,
  buttonLabel,
  buttonDisabled,
  buttonTooltip,
  buttonDataTestId,
  inputMaxWidth,
  showButton = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleButtonClick = async () => {
    if (onButtonClick) {
      setIsLoading(true);
      try {
        await onButtonClick();
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <InputWithRightButtonWrapper
      style={inputMaxWidth ? { maxWidth: inputMaxWidth } : undefined}
    >
      <InheritTextInputField
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        data-testid={dataTestId}
        style={
          showButton
            ? {
                paddingRight: "62px",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }
            : undefined
        }
      />
      {showButton && (
        <InputRightButton
          type="button"
          onClick={handleButtonClick}
          disabled={buttonDisabled || isLoading}
          title={buttonTooltip}
          aria-label={typeof buttonLabel === "string" ? buttonLabel : undefined}
          data-testid={buttonDataTestId}
        >
          {isLoading ? (
            <CircularProgress size={12} sx={{ color: "#ff8038" }} />
          ) : (
            buttonLabel ?? "Action"
          )}
        </InputRightButton>
      )}
    </InputWithRightButtonWrapper>
  );
};

export default InputWithButton;
